﻿import React, { Fragment, useEffect, useState } from "react";
import moment from "moment";
import PropTypes from 'prop-types';

import { ShowHide } from "components/layout";
import { StatusBlock } from 'components/status';
import { FormRadio, FormDateInput, FormSwitch, FormTextInput, FormHelperText, FormHeading } from "components/form-fields";
import { Table } from 'components/listing';
import { useValidation } from "hooks/useValidation";

export function DebriefGeneralSettings(
    {
        disabled = false,
        loading = false,
        debriefSignoffMethods,
        onUpdate,
        onValidationUpdate,
        saving,
        settings
    }
) {

    const [isValid, errors, validate] = useValidation();
    const [levelsValid, setLevelsValid] = useState(true);
    const [debriefSignoffMethodRows, setDebriefSignoffMethodRows] = useState([]);
    const signoffMethodtableHeaders = {
        headers: [
            { key: 'TYPE', value: 'Type' },
            { key: 'DESCRIPTION', value: 'Description' },
            { key: 'METHODS', value: '' }
        ]
    };

    useEffect(validateSettings, [settings]);

    useEffect(() => onValidationUpdate(isValid && levelsValid), [isValid, levelsValid]);

    useEffect(() => {
        formatDebriefSignoffMethods();
    }, [debriefSignoffMethods, settings, loading, saving]);

    function validateSettings() {
        let rules = [];

        if (settings.customStartDate) {
            rules.push({ fieldName: "startDate", required: true });
        }

        let externalRules = [];
        if (settings.debriefSignoffMethods.length === 0) {
            externalRules.push({
                fieldName: "debriefSignoffMethods",
                valid: false,
                errorMessage: "At least one signoff method must be selected."
            });
        }

        var areLevelsValid = true;

        settings.levels.forEach(function (level) {
            if (level.name == null || level.name.length === 0) {
                areLevelsValid = false;
            }
        });

        setLevelsValid(areLevelsValid);

        validate(rules, settings, externalRules);
    }

    function onCustomStartDateChange(item) {
        onUpdate({
            ...settings,
            customStartDate: item.currentTarget.value === "date"
        });
    }

    function onStartDateChange(item) {
        onUpdate({
            ...settings,
            startDate: item ? moment(item).format("YYYY-MM-DD") : null
        });
    }

    function onStartDateOverride(item) {
        onUpdate({
            ...settings,
            customStartDateOverideAllowed: item.currentTarget.checked
        });
    }

    function onDisableSupressAllOption(item) {
        onUpdate({
            ...settings,
            disableSupressAllOption: item.currentTarget.checked
        });
    }

    function onRequireManagerSignature(item) {
        onUpdate({
            ...settings,
            requireManagerSignature: item.currentTarget.checked
        });
    }

    function onAllowDebriefLevels(item) {
        onUpdate({
            ...settings,
            debriefLevelsActive: item.currentTarget.checked
        });
    }

    function onDebriefLevelsMandatory(item) {
        onUpdate({
            ...settings,
            debriefLevelsMandatory: item.currentTarget.checked
        });
    }

    function onLevelNameChange(id, item) {

        var levels = [
            ...settings.levels
        ];

        var level = levels.find(l => l.id === id);
        level.name = item.target.value;

        onUpdate({
            ...settings,
            levels: levels
        });
    }

    function onDebriefSignoffMethodChange(items) {
        onUpdate({
            ...settings,
            debriefSignoffMethods: items
        });
    }

    function formatParamTableHeaders() {

        let paramHeaders = ["Debrief Level", "Debrief Level Name"];

        return {
            headers: paramHeaders.map((value) => {
                return {
                    key: value,
                    value: value
                }
            })
        };
    }

    function formatParamTable() {
        return settings.levels.map(function (level) {
            return {
                cells: [
                    <span className="debrief-level-status"><StatusBlock background={level.background}>{level.id}</StatusBlock></span>,
                    <FormTextInput loading={loading} disabled={saving} value={level.name} id={`input-debrief-level-${level.id}`} onChange={(ev) => { onLevelNameChange(level.id, ev) }} />
                ],
                key: level.id
            };
        });
    }

    function formatDebriefSignoffMethods() {
        if (debriefSignoffMethods == null || debriefSignoffMethods.length === 0) {
            return;
        }

        let rows = [];

        debriefSignoffMethods.map(function (m) {

            let currentlyEnabled = settings.debriefSignoffMethods.some(s => s === m.debriefSignoffMethodKey);

            rows.push({
                cells: [
                    m.debriefSignoffMethodName,
                    m.description,
                    <FormSwitch
                        onChange={(s) => { onDebriefSignoffMethodSwitchChange(s.currentTarget.checked, m.debriefSignoffMethodKey) }}
                        checked={currentlyEnabled}
                        loading={loading}
                        disabled={saving}
                        key={`signoff-method-${m.debriefSignoffMethodKey}`}
                        id={`signoff-method-switch-${m.debriefSignoffMethodKey}`}
                    ></FormSwitch>
                ],
                key: m.debriefSignoffMethodKey
            });
        });

        setDebriefSignoffMethodRows(rows);
    }

    function onDebriefSignoffMethodSwitchChange(checked, key) {
        let newSignoffMethods = [...settings.debriefSignoffMethods];

        if (!checked) {
            newSignoffMethods = newSignoffMethods.filter(s => s !== key);
        } else if (!newSignoffMethods.some(s => s.debriefSignoffMethodKey === key)) {
            newSignoffMethods.push(key);
        }

        onDebriefSignoffMethodChange(newSignoffMethods);
    }

    return (
        <div>
            <FormRadio
                disabled={saving}
                onChange={onCustomStartDateChange}
                headingText="How should the debrief start date be calculated:"
                selectedValue={settings.customStartDate ? "date" : "default"}
                loading={loading}
                options={[{ value: "default", label: "Use default debrief start date" }, { value: "date", label: "Set debrief start date for the account" }]}
                id="input-debrief-use-custom-date"
            ></FormRadio>
            <ShowHide
                evaluator={settings.customStartDate}
                show={(
                    <Fragment>
                        <FormDateInput
                            disabled={saving}
                            headingText="Start date:"
                            onChange={onStartDateChange}
                            value={settings.startDate}
                            loading={loading}
                            minDate={moment("2010-01-01").toDate()}
                            id="input-debrief-custom-date"
                            dangerHelperText={errors.startDate}
                        ></FormDateInput>

                        <FormSwitch
                            disabled={saving}
                            label="Allow start date to be overriden:"
                            onChange={onStartDateOverride}
                            checked={settings.customStartDateOverideAllowed}
                            loading={loading}
                            id="input-start-date-overide"
                            helperText="Selecting yes will allow users of the debrief to amend the date set for the first debrief undertaken. Selecting no will ensure that the date is fixed for all drivers unless the start date on their driver profile is later than the date set."
                        ></FormSwitch>
                    </Fragment>
                )}
            />
            <FormSwitch
                disabled={saving}
                label="Disable ability to debrief all infringements and events at once:"
                onChange={onDisableSupressAllOption}
                checked={settings.disableSupressAllOption}
                loading={loading}
                id="input-debrief-all-infringements-events"
                helperText="Selecting yes will disable the ability to debrief all infringements and events at once. Selecting no will allow the user to debrief all infringements and events at once."
            ></FormSwitch>
            <h3>Debrief levels</h3>
            <FormSwitch
                disabled={saving}
                label="Enable debrief levels:"
                onChange={onAllowDebriefLevels}
                checked={settings.debriefLevelsActive}
                loading={loading}
                id="input-enable-debrief-levels"
                helperText="Determines whether a debrief level can be set during the debrief process. Setting this to 'off' will remove the debrief level selection."
            ></FormSwitch>
            <ShowHide
                evaluator={settings.debriefLevelsActive}
                show={(
                    <Fragment>
                        <FormSwitch
                            disabled={saving}
                            label="Make debrief levels mandatory:"
                            onChange={onDebriefLevelsMandatory}
                            checked={settings.debriefLevelsMandatory}
                            loading={loading}
                            id="input-debrief-levels-mandatory"
                            helperText="If enabled, a debrief level must be selected when performing a debrief."
                        ></FormSwitch>

                        <div className='spacer-bottom'>
                            <h4>Configure debrief level names:</h4>
                            <Table
                                headerData={formatParamTableHeaders()}
                                data={formatParamTable()}
                                loadingData={false}
                                fitToContent={false}
                            />
                            <ShowHide
                                evaluator={!levelsValid}
                                show={(
                                    <div className="form-field">
                                        <FormHelperText loading={loading} danger={true}>All levels must have a name</FormHelperText>
                                    </div>
                                )}
                            />
                        </div>

                    </Fragment>
                )}
            />
            <h3>Debrief Sign off</h3>
            <FormSwitch
                disabled={saving}
                label="Require manager e-signature:"
                onChange={onRequireManagerSignature}
                checked={settings.requireManagerSignature}
                loading={loading}
                id="input-require-manager-e-signature"
                helperText="Selecting yes will require the managers e-signature when completing the debrief."
            ></FormSwitch>
            <div className="spacer-bottom">
                <FormHeading loading={loading} headingLevel={"h4"}>Debrief sign off methods</FormHeading>
                <Table
                    headerData={signoffMethodtableHeaders}
                    data={debriefSignoffMethodRows}
                    loadingData={loading}
                    fitToContent={false}
                    dangerHelperText={errors.debriefSignoffMethods}
                />
            </div>
        </div>
    );
}

DebriefGeneralSettings.propTypes = {
    settings: PropTypes.object.isRequired,
    saving: PropTypes.bool,
    loading: PropTypes.bool,
    onValidationUpdate: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired,
    debriefSignoffMethods: PropTypes.array.isRequired
};